import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import { EMAIL_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { Icon } from "@circleco/compass/components/Icon";
import { Link } from "@circleco/compass/components/Link";
import { TextInput } from "@circleco/compass/components/TextInput";
import type { FocusEvent } from "react";
import type { FieldError } from "react-hook-form";
import { get, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

import { useEmailVerificationInitialState } from "./hooks/useEmailVerificationInitialState";

const LOCALE_ROOT =
  "paywall_checkout.form.email_verification_states.initial_state";

interface EmailVerificationInitialStateProps {
  isValidating?: boolean;
  onAuthenticationLinkClick?: () => void;
  shouldShowAuthenticationLink?: boolean;
}

export const EmailVerificationInitialState = ({
  isValidating = false,
  shouldShowAuthenticationLink = false,
  onAuthenticationLinkClick,
}: EmailVerificationInitialStateProps) => {
  const { handleBlur, handleAuthenticationClick } =
    useEmailVerificationInitialState({
      onAuthenticationLinkClick,
    });
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const emailRegistration = register("email", {
    required: true,
    pattern: EMAIL_REGEX,
  });
  const emailError = get(errors, "email") as FieldError | undefined;

  const handleEmailBlur = async (event: FocusEvent<HTMLInputElement>) => {
    await emailRegistration.onBlur(event);
    handleBlur();
  };

  return (
    <div
      className="flex flex-col gap-2 p-6"
      data-testid="checkout-form-email-verification-initial-state"
    >
      <TextInput
        {...emailRegistration}
        onBlur={handleEmailBlur}
        invalid={!!emailError}
        error={
          emailError &&
          t(`paywall_checkout.form.errors.email_${emailError.type}`)
        }
        label={t("paywall_checkout.form.fields.email")}
        endContent={isValidating ? <Loader className="p-0" /> : undefined}
        data-testid="checkout-form-email-input"
      />
      {shouldShowAuthenticationLink && (
        <div className="flex items-center gap-1.5 pt-2">
          <Icon name="circle-info" size="md" color="tertiary" />
          <Typography.LabelXs weight="normal" color="text-tertiary">
            <Link
              asButton
              disabled={isValidating}
              variant="custom"
              color="tertiary"
              hoverColor="primary"
              hoverDecoration="underline"
              typography="label-xs"
              onClick={handleAuthenticationClick}
            >
              {t([LOCALE_ROOT, "authenticate_account"])}
            </Link>
            {` ${t([LOCALE_ROOT, "before_completing_checkout"])}`}
          </Typography.LabelXs>
        </div>
      )}
    </div>
  );
};
